import { message, Modal } from 'ant-design-vue'

let Axios = require('axios');

let authExpiredModalVisible = false
let authExpiredRedirecting = false

const HTTP_ERROR_MESSAGE_MAP = {
  400: '请求参数错误，请检查后重试',
  401: '认证失败，请重新登录',
  403: '暂无权限执行此操作',
  404: '请求的资源不存在',
  405: '请求方式不受支持',
  408: '服务器请求超时，请稍后再试',
  409: '请求数据发生冲突，请刷新后重试',
  413: '上传内容过大，请调整后重试',
  422: '请求参数校验失败，请检查后重试',
  429: '请求过于频繁，请稍后再试',
  500: '服务器内部错误，请稍后再试',
  502: '服务器网关异常，请稍后再试',
  503: '服务器暂时不可用，请稍后再试',
  504: '服务器请求超时，请稍后再试'
}

export function resetAuthExpiredState() {
  authExpiredModalVisible = false
  authExpiredRedirecting = false
}

Axios.defaults.baseURL = process.env.VUE_APP_BASE_API || ''
Axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded'

function clearAuthStorage() {
  localStorage.removeItem('token')
  localStorage.removeItem('userInfo')
  localStorage.removeItem('roleInfo')
  localStorage.removeItem('schoolId')
  localStorage.removeItem('schoolInfo')
}

function getToken() {
  return localStorage.getItem('token') || ''
}

function getHeaders(headers = {}) {
  const token = getToken()
  return token
    ? Object.assign({}, headers, { Authorization: token })
    : headers
}

function getCurrentPath() {
  return window.location.hash ? window.location.hash.replace(/^#/, '') : window.location.pathname
}

function isAuthPage(path = getCurrentPath()) {
  return /^\/(login|register|forgetPassword|404)(?:[/?#]|$)/.test(path)
}

function redirectToLogin(currentPath = getCurrentPath()) {
  if (currentPath.indexOf('/login') === 0) return
  const canRedirect = currentPath && currentPath !== '/' && !isAuthPage(currentPath)
  const redirect = canRedirect ? `?redirect=${encodeURIComponent(currentPath)}` : ''
  window.location.replace(`/#/login${redirect}`)
}

function isAppLoading() {
  const app = document.getElementById('app')
  return Boolean(app && app.querySelector('.app-loading'))
}

function handleAuthExpiredState() {
  if (!isAppLoading()) return showAuthExpiredModal()
  if (authExpiredRedirecting) return true

  authExpiredRedirecting = true
  clearAuthStorage()
  redirectToLogin()
  return true
}

function showAuthExpiredModal() {
  if (isAuthPage()) return false
  if (authExpiredModalVisible) return true

  const currentPath = getCurrentPath()
  authExpiredModalVisible = true
  clearAuthStorage()
  Modal.confirm({
    class: 'auth-expired-modal',
    width: 560,
    title: '登录状态已过期',
    content: '当前登录状态已过期，请重新登录后继续操作。',
    icon: 'exclamation-circle',
    okText: '重新登录',
    cancelText: '关闭',
    centered: true,
    keyboard: false,
    maskClosable: false,
    onOk() {
      resetAuthExpiredState()
      redirectToLogin(currentPath)
    },
    onCancel() {
      resetAuthExpiredState()
    },
    afterClose() {
      resetAuthExpiredState()
    }
  })
  return true
}

function handleAuthExpired(error) {
  const status = error && error.response && error.response.status
  if (status === 401) {
    return handleAuthExpiredState()
  }
  return false
}

function getResponseErrorMessage(error) {
  const data = error && error.response && error.response.data
  return data && (data.msg || data.message)
}

function getRequestErrorMessage(error) {
  const response = error && error.response
  const status = response && response.status
  const msg=response && response.data ? response.data.msg:''
  const statusMessage = HTTP_ERROR_MESSAGE_MAP[status]
  const isTimeout = error && (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || ''))

  if (isTimeout) return '服务器请求超时，请稍后再试'
  if (status >= 500 || status === 408 || status === 429) return msg || statusMessage || '服务器异常，请稍后再试'

  const responseMessage = getResponseErrorMessage(error)
  if (responseMessage) return responseMessage
  if (statusMessage) return statusMessage
  if (!response) return '网络连接异常，请检查网络后重试'
  return '请求失败，请稍后再试'
}

function handleRequestError(error, options = {}) {
  if (handleAuthExpired(error)) return
  if (options.silent) return
  message.error(getRequestErrorMessage(error))
}

function handleResponseData(data) {
  if (data && Number(data.code) === 401) {
    handleAuthExpiredState()
  }
  return data
}

function quoteLongNumberFields(data, fields = []) {
  if (typeof data !== 'string' || !fields.length) return data
  return fields.reduce((text, field) => {
    const pattern = new RegExp(`("${field}"\\s*:\\s*)(-?\\d{16,})(?=\\s*[,}\\]])`, 'g')
    return text.replace(pattern, '$1"$2"')
  }, data)
}

function parseResponseText(data, stringFields) {
  if (typeof data !== 'string') return data
  const text = quoteLongNumberFields(data, stringFields)
  try {
    return JSON.parse(text)
  } catch (error) {
    return data
  }
}

export function fetch(url, params = {}) {
  return new Promise((resolve, reject) => {
    Axios.post(url, params, {
      headers: getHeaders()
    })
      .then(response => {
        resolve(handleResponseData(response.data));
      })
      .catch((error) => {
        handleRequestError(error)
        reject(error);
      })
  })
}

export function fetchGet(url, params = {}) {
  return new Promise((resolve, reject) => {
    Axios.get(url, {
      params,
      headers: getHeaders()
    })
      .then(response => {
        resolve(handleResponseData(response.data));
      })
      .catch((error) => {
        handleRequestError(error)
        reject(error);
      })
  })
}

export function fetchWithStringFields(url, params = {}, stringFields = []) {
  return new Promise((resolve, reject) => {
    Axios.post(url, params, {
      headers: getHeaders(),
      transformResponse: [data => parseResponseText(data, stringFields)]
    })
      .then(response => {
        resolve(handleResponseData(response.data));
      })
      .catch((error) => {
        handleRequestError(error)
        reject(error);
      })
  })
}

export function fetchJson(url, params = {}, options = {}) {
  return new Promise((resolve, reject) => {
    Axios.post(url, params, {
      headers: getHeaders({
        'Content-Type': 'application/json'
      })
      })
      .then(response => {
        resolve(handleResponseData(response.data));
      })
      .catch((error) => {
        handleRequestError(error, options)
        reject(error);
      })
  })
}

export function upload(url, formData = {}) {
  return new Promise((resolve, reject) => {
    Axios.post(url, formData, {
      headers: getHeaders()
      })
      .then(response => {
        resolve(handleResponseData(response.data));
      })
      .catch((error) => {
        handleRequestError(error)
        reject(error);
      })
  })
}



// WEBPACK FOOTER //
// ./src/api/base.js